/**
 * 「当前积分」显示口径 —— ✅ **全项目只有这一处**。
 *
 * ## 为什么要单独抽出来
 *
 * 2026-10-14 孩子详情页的真实隐患：那个大数字写的是
 *
 *     pointsQuery.data?.balance ?? student.pointsBalance ?? 0
 *
 * 而两个来源都**拿不到真值**：
 *   - `GET /students/:id` **不返回** `pointsBalance`（只有列表接口返回），
 *     所以 `student.pointsBalance` 恒为 `undefined`；
 *   - `studentPoints()` 在 404/501 时兜底成 `{ balance: 0 }`。
 *
 * 于是**积分接口一旦失败，页面显示"0 积分"**，看起来像一个正常的余额。
 * 这与本项目当天刚修过的"孩子卡片显示 0 积分""统计页恒为 0"是同一类病：
 * **拿一个假值冒充真值**。
 *
 * ## 三种情形（互不冒充）
 *
 * | 情形 | 判据 | 显示 |
 * | --- | --- | --- |
 * | 真的读到了 | `balance` 是有限数字（**包括 0**） | 那个数字，0 就写「0」 |
 * | 拿不到 | 查询失败 / 接口未就绪（`data === null`）| 「—」 |
 * | 还在读 | 查询 loading | 加载态（骨架），**不是** 0、**也不是**「—」 |
 *
 * ⚠️ 顺序很重要：**真的 0 分与失败必须分开**，所以先看"有没有读到数字"，
 *   再看"是不是还在加载"，最后才是「—」。
 * ⚠️ 纯函数：无 React / DOM 依赖，可以被用例直接喂三种输入。
 */

import type { PointsSummary } from './api/types';

/** 拿不到真实余额时的占位符 —— 与 `@fht/shared` 的 `DURATION_PLACEHOLDER` 同一个约定。 */
export const POINTS_PLACEHOLDER = '—';

/** 当前积分这一格该显示"一个数字"、"加载中"还是"拿不到"。 */
export type BalanceDisplay = 'value' | 'loading' | 'unknown';

export interface BalanceDisplayInput {
  /**
   * 积分接口的结果。
   *  - 查询成功 → `PointsSummary`（`balance` 真的是 0 就是 0）
   *  - 查询失败 / 未就绪 → `null`（见 `students.ts` 的 `studentPoints()`）
   *  - 还没回来 → `undefined`
   */
  points: PointsSummary | null | undefined;
  /** 积分查询是否还在加载。 */
  pointsLoading: boolean;
  /**
   * 学生档案里顺带带的余额（只有列表接口会给）。
   * `null` / `undefined` = 没读到，**不等于 0 分**。
   */
  profileBalance: number | null | undefined;
  /** 学生档案是否还在加载（用来区分"积分失败但档案还没读回来"）。 */
  profileLoading?: boolean;
}

/**
 * 判定「当前积分」该显示什么。
 *
 * 取值顺序（每一步都有理由，不要调换）：
 *  1. `points.balance` 是有限数字 → `'value'`：**真的 0 分也走这一支**，显示「0」。
 *  2. 档案里带了有限数字 → `'value'`：`/students/:id/points` 不可用时，
 *     列表接口给的余额仍是**真实数据**（详情页拿不到这个字段，但保留这条兜底）。
 *  3. 积分查询还在加载 → `'loading'`。
 *  4. 档案还在加载（余额可能随后就到）→ `'loading'`。
 *  5. 其余 → `'unknown'`（界面显示 `POINTS_PLACEHOLDER`）。
 */
export function resolveBalanceDisplay(input: BalanceDisplayInput): BalanceDisplay {
  const { points, pointsLoading, profileBalance, profileLoading = false } = input;

  const fromPoints = points?.balance;
  if (typeof fromPoints === 'number' && Number.isFinite(fromPoints)) return 'value';

  if (typeof profileBalance === 'number' && Number.isFinite(profileBalance)) return 'value';

  if (pointsLoading) return 'loading';
  if (profileLoading) return 'loading';

  return 'unknown';
}

/** 要写进那个大数字里的文案（`'loading'` 交给调用方渲染骨架，所以返回 `null`）。 */
export function balanceDisplayText(input: BalanceDisplayInput): string | null {
  const state = resolveBalanceDisplay(input);
  if (state === 'loading') return null;

  // 不依赖 state 就断言"一定有数字"：两个来源各查一次 `Number.isFinite`。
  // 没有有限数字时给「—」，**绝不**把 `undefined` 拼成字符串写上屏。
  const fromPoints = input.points?.balance;
  if (typeof fromPoints === 'number' && Number.isFinite(fromPoints)) return String(fromPoints);

  const fromProfile = input.profileBalance;
  if (typeof fromProfile === 'number' && Number.isFinite(fromProfile)) return String(fromProfile);

  return POINTS_PLACEHOLDER;
}

// ---------------------------------------------------------------- 单笔流水的金额

/**
 * 一条流水的金额该用哪种语气：正数绿色 `+N`、负数红色 `-N`。
 *
 * 显示层**本来是对的**（2026-10-18 的「扣分显示成 +10」不是它的问题：
 * 那条流水在数据库里就是 `delta=+10`，界面只是如实画出来）。抽成函数是为了
 * 让"负数必须红、必须带负号"有一条可断言的判据，而不是散在两处 JSX 里。
 */
export type LedgerAmountTone = 'positive' | 'negative';

/** 金额的语气（负数 → 红色）。注意 0 归到 positive（账本不允许 0，这里只是兜底）。 */
export function ledgerAmountTone(delta: number): LedgerAmountTone {
  return delta < 0 ? 'negative' : 'positive';
}

/** 金额文案：正数补 `+`，负数保留自己的 `-`（`-10` 不能再写成 `+-10`）。 */
export function ledgerAmountText(delta: number): string {
  return `${ledgerAmountTone(delta) === 'positive' ? '+' : ''}${delta}`;
}

/** 金额的配色（`text-emerald-600` / `text-rose-600`）—— 家长端与学生端共用。 */
export const LEDGER_AMOUNT_TONE_CLASS: Record<LedgerAmountTone, string> = {
  positive: 'text-emerald-600',
  negative: 'text-rose-600',
};

// ---------------------------------------------------------------- 积分概览四格

/** 数字→文案；`null` / `undefined` / 非有限数一律「—」（**不是 0**）。 */
function numberOrPlaceholder(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : POINTS_PLACEHOLDER;
}

/**
 * 「累计获得」：正数带 `+`；拿不到就是「—」。
 *
 * 口径见 `apps/api/src/lib/points.ts`：只算 `checkin` / `makeup` / `bonus`，
 * **不含** `adjust`（冲正 / 调账）与 `penalty`（扣分）。
 */
export function earnedTotalText(earned: number | null | undefined): string {
  if (typeof earned !== 'number' || !Number.isFinite(earned)) return POINTS_PLACEHOLDER;
  return `+${earned}`;
}

/**
 * 「累计消费」：只算 `redeem`（`redeem_refund` 冲减）。
 *
 * 真 0 分要显示「0」而不是「-0」；拿不到才显示「—」。
 * ⚠️ 2026-10-18 的 bug 是把这个数字算成了"所有负数流水"：-70 里有 -60 是**冲正**，
 * 一分钱也没兑换出去，却被当成消费显示了。
 */
export function spentTotalText(spent: number | null | undefined): string {
  if (typeof spent !== 'number' || !Number.isFinite(spent)) return POINTS_PLACEHOLDER;
  return spent === 0 ? '0' : `-${spent}`;
}

/**
 * 「本周 / 本月变化」：`400 / 400`。
 *
 * 两个值都缺失时是 `— / —`（旧接口）；只有一边缺失时那一边单独显示「—」，
 * 仍然**不会**用 0 顶替 —— 这正是「一直显示 0 / 0」那个 bug 的教训。
 */
export function weekMonthChangeText(
  week: number | null | undefined,
  month: number | null | undefined,
): string {
  return `${numberOrPlaceholder(week)} / ${numberOrPlaceholder(month)}`;
}

export interface PointsBreakdownInput {
  balance: number | null | undefined;
  earned: number | null | undefined;
  spent: number | null | undefined;
  penalty: number | null | undefined;
  adjustment: number | null | undefined;
}

/**
 * 口径核对行：`400 = 获得 460 − 消费 0 − 扣分 0 + 调账 (−60)`。
 *
 * 为什么值得写在页面上：家长看到"余额 400 / 获得 470 / 消费 70"三个数加不起来时，
 * 会怀疑系统在乱算（2026-10-18 用户的原话就是"这 70 是冲正造成的，不是消费"）。
 * 把「扣分」和「调账·冲正」单列出来，恒等式当场成立 —— 不是硬凑，是口径写清楚了。
 *
 * 每一项都带中文标签：只写 `400 = 460 − 0 − 0 + (−60)` 的话，
 * 读者还得回头数"第二个 0 是消费还是扣分"。
 *
 * 任一项缺失（旧接口）返回 `null`，页面就不显示这行，而不是画一个假等式。
 */
export function pointsBreakdownText(input: PointsBreakdownInput): string | null {
  const values = [input.balance, input.earned, input.spent, input.penalty, input.adjustment];
  if (!values.every((value) => typeof value === 'number' && Number.isFinite(value))) return null;

  const balance = input.balance as number;
  const earned = input.earned as number;
  const spent = input.spent as number;
  const penalty = input.penalty as number;
  const adjustment = input.adjustment as number;

  // 负数用括号包起来，避免出现 "调账 − -60" 这种读不懂的写法
  const signed = adjustment < 0 ? `(−${Math.abs(adjustment)})` : String(adjustment);
  return `${balance} = 获得 ${earned} − 消费 ${spent} − 扣分 ${penalty} + 调账 ${signed}`;
}
